import { useTranslation } from 'react-i18next';
import type { RMFiscalPeriodType as PeriodType } from '@/lib/api/types/roadmapping';

export interface DateHeaderSubMenuProps {
  onValueSelected: (value: string) => void;
  onClose: () => void;
  value?: string;
}

const PERIOD_TYPES: PeriodType[] = ['week', 'month', 'quarter', 'half', 'year'] as const;

export function DateHeaderSubMenu({ onValueSelected, onClose, value }: DateHeaderSubMenuProps) {
  const { t } = useTranslation();

  const handlePeriodSelect = (dateType: 'startDate' | 'endDate', periodType: PeriodType) => {
    const selectedValue = `${dateType}:${periodType}`;
    onValueSelected(selectedValue);
    onClose();
  };

  const getOptionClassName = (dateType: 'startDate' | 'endDate', periodType: PeriodType) => {
    const optionValue = `${dateType}:${periodType}`;
    const isSelected = value === optionValue;

    return `w-full px-3 py-2 text-left text-sm cursor-pointer transition-colors ${
      isSelected
        ? 'bg-bg-brand text-text-brand-inverted rounded'
        : 'hover:bg-bg-neutral-subtler-hovered'
    }`;
  };

  return (
    <div className="py-2 min-w-[200px] max-w-[350px]">
      <div className="px-3 py-1.5 text-xs font-medium text-text-neutral-secondary uppercase tracking-wide">
        {t('roadmapping.layout.headers.fields.itemStartDate')}
      </div>
      {PERIOD_TYPES.map((periodType) => (
        <button
          key={`start-${periodType}`}
          type="button"
          className={getOptionClassName('startDate', periodType)}
          onClick={() => handlePeriodSelect('startDate', periodType)}
        >
          {t(`roadmapping.layout.headers.fields.period.${periodType}`)}
        </button>
      ))}

      <div className="border-t border-border-neutral-subtle my-1" />
      <div className="px-3 py-1.5 text-xs font-medium text-text-neutral-secondary uppercase tracking-wide">
        {t('roadmapping.layout.headers.fields.itemEndDate')}
      </div>
      {PERIOD_TYPES.map((periodType) => (
        <button
          key={`end-${periodType}`}
          type="button"
          className={getOptionClassName('endDate', periodType)}
          onClick={() => handlePeriodSelect('endDate', periodType)}
        >
          {t(`roadmapping.layout.headers.fields.period.${periodType}`)}
        </button>
      ))}
    </div>
  );
}
